import { useEffect, useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';
import type { RoutesFilter, RoutesListSharedFilterFields } from '../models';
import useRouteNameSuggestions from './useRouteNameSuggestions';

const ROUTE_NAME_DEBOUNCE_MS = 300;

interface UseRoutesSearchRouteNameLiveFilterProps {
  appliedRoutesFilter: RoutesFilter;
  localRouteName: string | undefined;
  mergeRoutesFilter: (partialRoutesFilter: Partial<RoutesFilter>) => void;
}

const useRoutesSearchRouteNameLiveFilter = ({
  appliedRoutesFilter,
  localRouteName,
  mergeRoutesFilter,
}: UseRoutesSearchRouteNameLiveFilterProps) => {
  const sharedListFiltersForSuggestions = useMemo(
    (): RoutesListSharedFilterFields => ({
      driverIds: appliedRoutesFilter.driverIds,
      logisticOperatorId: appliedRoutesFilter.logisticOperatorId,
      commerceId: appliedRoutesFilter.commerceId,
      scheduledFrom: appliedRoutesFilter.scheduledFrom,
      scheduledTo: appliedRoutesFilter.scheduledTo,
      shippingNumber: appliedRoutesFilter.shippingNumber,
      status: appliedRoutesFilter.status,
    }),
    [
      appliedRoutesFilter.driverIds,
      appliedRoutesFilter.logisticOperatorId,
      appliedRoutesFilter.commerceId,
      appliedRoutesFilter.scheduledFrom,
      appliedRoutesFilter.scheduledTo,
      appliedRoutesFilter.shippingNumber,
      appliedRoutesFilter.status,
    ]
  );

  const trimmedRouteNameFromLocal = (localRouteName ?? '').trim();
  const debouncedTrimmedRouteName = useDebounce(
    trimmedRouteNameFromLocal,
    ROUTE_NAME_DEBOUNCE_MS
  );

  useEffect(() => {
    const currentTrimmedRouteName = (localRouteName ?? '').trim();
    if (debouncedTrimmedRouteName !== currentTrimmedRouteName) {
      return;
    }
    const nextRouteName =
      debouncedTrimmedRouteName.length > 0
        ? debouncedTrimmedRouteName
        : undefined;
    if (appliedRoutesFilter.routeName === nextRouteName) {
      return;
    }
    mergeRoutesFilter({ routeName: nextRouteName });
  }, [
    appliedRoutesFilter.routeName,
    debouncedTrimmedRouteName,
    localRouteName,
    mergeRoutesFilter,
  ]);

  const { routeNameOptions, isRouteNamesLoading } = useRouteNameSuggestions({
    appliedSharedFilters: sharedListFiltersForSuggestions,
    routeNameSearchTerm: debouncedTrimmedRouteName,
  });

  return {
    routeNameOptions,
    isRouteNamesLoading,
  };
};

export default useRoutesSearchRouteNameLiveFilter;
